import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { IconButton, OutlinedInput, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { Params, useLocation, useNavigate, useParams } from "react-router-dom";
import Table from "../../../../ui/components/table/table";
import { Convenio } from "../../../models/convenio";
import { ConvenioPractica } from "../../../models/convenio-practica";
import { convenioServices } from "../../../services/convenio.services";
import { getFormattedDecimal } from "../../../utils/common.utils";
import {
  initialFilter,
  queryParamsConfig,
} from "../../../utils/convenio-practica.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import ConvenioPracticaEditModal from "../convenio-practica-edit-modal/convenio-practica-edit-modal";
import "./convenio-practicas-edit.scss";

export default function ConvenioPracticasEdit() {
  const { id } = useParams<Params>();
  const [queryParams, setQueryParams] =
    useQueryParamsReplace(queryParamsConfig);
  const [entities, setEntities] = useState<ConvenioPractica[]>([]);
  const [convenio, setConvenio] = useState<Convenio>();
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  const [editingPractica, setEditingPractica] =
    useState<ConvenioPractica | null>(null);

  useEffect(() => {
    if (id) {
      convenioServices
        .getById(id)
        .then((response) => setConvenio(response.data))
        .catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (location.search === "") {
      setQueryParams(initialFilter);
    }
    if (queryParams.page && id) {
      getPracticasConvenio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, id]);

  const getPracticasConvenio = async () => {
    if (!id) return;
    let params = { ...queryParams, active: true };
    convenioServices
      .getPracticasList(id, params)
      .then((response) => {
        setEntities(response.data.items);
        setTotalCount(response.data.totalItems);
      })
      .catch(() => {});
  };

  const handleCloseModal = (updated?: ConvenioPractica) => {
    setEditingPractica(null);
    if (updated) {
      getPracticasConvenio();
    }
  };

  const columns: TableColumn<ConvenioPractica>[] = [
    {
      name: "Nº Práctica",
      selector: (row) => row.numeroPractica,
      width: "12%",
      sortable: true,
      sortField: "numero_practica",
    },
    {
      name: "Nombre",
      selector: (row) => row.descripcion,
      sortable: true,
      sortField: "nombre",
    },
    // Only show Antigüedad columns if convenio?.consideraAntiguedad !== false
    ...(convenio?.consideraAntiguedad !== false
      ? [
          {
            name: "Antigüedad Mínima",
            selector: (row: ConvenioPractica) => row.antiguedadMinima,
            width: "12%",
          },
          {
            name: "Antigüedad Máxima",
            selector: (row: ConvenioPractica) => row.antiguedadMaxima,
            width: "12%",
          },
        ]
      : []),
    {
      name: "Precio",
      cell: (row) => (
        <div className="table-cell-right">
          {getFormattedDecimal(row.precio)}
        </div>
      ),
      width: "12%",
    },
    {
      name: "Coseguro",
      cell: (row) => (
        <div className="table-cell-right">
          {getFormattedDecimal(row.coseguro)}
        </div>
      ),
      width: "12%",
    },
    {
      name: "Tipo",
      selector: (row) => row.tipo,
      width: "10%",
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <Tooltip title="Editar">
          <IconButton
            aria-label="editar"
            onClick={() => setEditingPractica(row)}
          >
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
      width: "10%",
      style: {
        position: "sticky",
        right: 0,
        backgroundColor: "#fff",
        zIndex: 1,
      },
    },
  ];

  return (
    <div className="convenio-practicas-edit-container">
      <div className="convenio-practicas-edit-header">
        <button
          type="button"
          className="convenio-back-btn"
          onClick={() => navigate(`/convenios/editar/${id}`)}
          title="Volver"
        >
          <ArrowBackIosNewOutlinedIcon fontSize="medium" />
        </button>
        <div>
          <h2>Editar Prácticas</h2>
          {convenio && <span className="convenio-nombre">{convenio.nombre}</span>}
        </div>
      </div>

      <div className="row action-row">
        <OutlinedInput
          type="text"
          placeholder="Nº de práctica o nombre"
          className="custom-input-search"
          autoComplete="off"
          onChange={(event) => {
            setQueryParams({
              ...queryParams,
              page: 1,
              search: event.target.value !== "" ? event.target.value : undefined,
            });
          }}
        ></OutlinedInput>
      </div>
      <div className="row">
        <div className="convenios-table">
          <Table
            table={{
              columns,
              data: entities,
              paginationTotalRows: totalCount,
              highlightOnHover: true,
            }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
          ></Table>
        </div>
      </div>

      <ConvenioPracticaEditModal
        show={!!editingPractica}
        convenioPractica={editingPractica}
        consideraAntiguedad={convenio?.consideraAntiguedad !== false}
        handleClose={handleCloseModal}
      />
    </div>
  );
}
